import { useEffect, useState } from "react"
import { ChevronDown } from "lucide-react"
import type { OpeningHourEntry, OpeningHoursStatus } from "../types"
import {
  computeOpeningStatus,
  type ComputedOpeningStatus,
} from "../../../utils/openingHoursStatus"

interface OpeningHoursProps {
  openingHoursList: OpeningHourEntry[]
  openingHoursStatus: OpeningHoursStatus
  temporarilyClosed?: boolean
}

export function OpeningHours({
  openingHoursList,
  openingHoursStatus,
  temporarilyClosed = false,
}: OpeningHoursProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [computed, setComputed] = useState<ComputedOpeningStatus | null>(null)

  useEffect(() => {
    setComputed(computeOpeningStatus(openingHoursStatus.periods))
  }, [openingHoursStatus.periods])

  // Fall back to server-provided values until client has computed fresh status
  const statusKey = computed?.statusKey ?? openingHoursStatus.status_key
  const statusTone = temporarilyClosed
    ? "soon"
    : (computed?.statusTone ??
      openingHoursStatus.status_tone ??
      (openingHoursStatus.is_open ? "open" : "closed"))
  const displayStatus = temporarilyClosed
    ? "Temporarily closed"
    : (computed?.statusLabel ??
      openingHoursStatus.status_label ??
      openingHoursStatus.current_status)
  const transitionText = temporarilyClosed
    ? "Check before you go"
    : (computed?.transitionText ?? openingHoursStatus.transition_text)

  const statusClasses = temporarilyClosed
    ? "bg-amber-600 text-white"
    : (() => {
        switch (statusTone) {
          case "open":
            return "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20"
          case "soon":
            return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/25"
          case "closed":
            return "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20"
          default:
            return "bg-surface-subtle text-foreground-secondary ring-1 ring-inset ring-foreground-subtle/20"
        }
      })()

  const hasStructuredStatus = Boolean(statusKey)
  const showFallbackHours =
    !hasStructuredStatus &&
    openingHoursStatus.today_hours &&
    !openingHoursStatus.today_hours.is_closed
  const showFallbackNextChange =
    !hasStructuredStatus && openingHoursStatus.next_change
  const isNoHours = statusKey === "no_hours"
  const canExpand = !isNoHours

  const todayName = new Date().toLocaleDateString("en-US", { weekday: "long" })

  return (
    <div
      className={
        temporarilyClosed
          ? "rounded-lg bg-amber-100 shadow-sm ring-1 ring-inset ring-amber-400"
          : "rounded-lg bg-card shadow-sm ring-1 ring-inset ring-border-strong"
      }
    >
      <div
        className={`flex items-center gap-3 px-3.5 py-4 ${isNoHours ? "justify-center" : ""}`}
      >
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-2 py-1 text-xs font-medium ${statusClasses}`}
        >
          {displayStatus}
        </span>

        {!isNoHours && (
          <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-2">
            {transitionText && (
              <span className="min-w-0 truncate text-right text-sm font-medium text-foreground-primary">
                <span className="text-foreground-faint">· </span>
                {transitionText}
              </span>
            )}
            {showFallbackHours && (
              <div className="text-sm font-medium text-foreground-primary">
                {openingHoursStatus.today_hours && (
                  <>
                    {openingHoursStatus.today_hours.open_time} -{" "}
                    {openingHoursStatus.today_hours.close_time}
                  </>
                )}
              </div>
            )}
            {showFallbackNextChange && (
              <div className="min-w-0 truncate text-right text-sm text-foreground-muted">
                {openingHoursStatus.next_change}
              </div>
            )}
            {canExpand && (
              <button
                type="button"
                className="flex shrink-0 items-center text-sm font-medium text-foreground-secondary hover:text-foreground-primary"
                onClick={() => setIsOpen(!isOpen)}
              >
                <ChevronDown
                  className={`h-5 w-5 text-foreground-faint transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
            )}
          </div>
        )}
      </div>

      {canExpand && isOpen && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-t border-border-default divide-x divide-border-default">
                <th
                  scope="col"
                  className="py-3.5 px-4 pr-4 text-left text-sm font-semibold text-foreground-primary"
                >
                  Day
                </th>
                <th
                  scope="col"
                  className="px-4 py-3.5 text-left text-sm font-semibold text-foreground-primary"
                >
                  Open
                </th>
                <th
                  scope="col"
                  className="px-4 py-3.5 text-left text-sm font-semibold text-foreground-primary"
                >
                  Close
                </th>
              </tr>
            </thead>
            <tbody>
              {openingHoursList.map((entry, i) => (
                <tr
                  key={i}
                  className={`border-t border-border-default divide-x divide-border-default ${
                    entry.start_day === todayName
                      ? "font-bold text-brand"
                      : "text-foreground-muted"
                  }`}
                >
                  <td className="whitespace-nowrap p-4 text-sm">
                    {entry.display_day}
                  </td>
                  <td className="whitespace-nowrap p-4 text-sm">
                    {entry.start_time}
                  </td>
                  <td className="whitespace-nowrap p-4 text-sm">
                    {entry.close_time}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
